import { http } from 'cdui-js';

/** int64 主键：接口以 string 传输/存储，避免 JSON number 精度丢失 */
export const normalizeCrsInt64Id = (id: unknown): string => {
  if (id == null || id === '') return '';
  if (typeof id === 'string') {
    const s = id.trim();
    return /^\d+$/.test(s) && !/^0+$/.test(s) ? s : '';
  }
  if (typeof id === 'number') {
    if (!Number.isFinite(id) || id <= 0) return '';
    return String(Math.trunc(id));
  }
  return normalizeCrsInt64Id(String(id));
};

/** UserCrsTaxInfoReqDTO */
export type UserCrsTaxInfoReq = {
  sortNo?: number;
  taxCountry?: string;
  /** TIN_PROVIDED / TIN_NOT_PROVIDED */
  taxStatus?: string;
  tin?: string;
  reasonCode?: string;
  reasonDesc?: string;
};

/** UserCrsSaveReqDTO（每次追加新版本） */
export type UserCrsSaveReq = {
  /** 编辑基于的版本 id（int64 字符串） */
  baseId?: string;
  /** INDIVIDUAL / ENTITY */
  entityType?: string;
  countryCode?: string;
  country?: string;
  provinceCode?: string;
  province?: string;
  cityCode?: string;
  city?: string;
  postcode?: string;
  /** APP / H5 */
  clientChannel?: string;
  remark?: string;
  taxInfos?: UserCrsTaxInfoReq[];
  /** 证明材料相对路径（私有上传返回的 filePath） */
  attachmentFilePaths?: string[];
};

/** UserCrsTaxInfoRespDTO */
export type UserCrsTaxInfo = UserCrsTaxInfoReq & {
  id?: string;
  crsId?: string;
};

/** UserAccountAttachmentRespDTO（CRS 只用到的字段） */
export type UserCrsAttachment = {
  id?: string;
  attachId?: string;
  attachType?: string;
  fileKey?: string;
  fileUrl?: string;
  filePath?: string;
  fileType?: string;
};

/** UserCrsRespDTO */
export type UserCrsRecord = {
  id?: string;
  userId?: string;
  reportYear?: number;
  version?: number;
  prevId?: string;
  /** DRAFT / SUBMITTED / APPROVING / REFUSED / EFFECTIVE */
  status?: string;
  /** 备注；拒绝时常为 JSON：[{"key":"rejectReason","message":"..."}] */
  remark?: string;
  entityType?: string;
  countryCode?: string;
  country?: string;
  provinceCode?: string;
  province?: string;
  cityCode?: string;
  city?: string;
  postcode?: string;
  baseId?: string;
  clientChannel?: string;
  taxInfos?: UserCrsTaxInfo[];
  attachments?: UserCrsAttachment[];
};

/** UserCrsLatestRespDTO */
export type UserCrsLatest = {
  record?: UserCrsRecord;
  /** 是否可在当年直接改 */
  editable?: boolean;
  /** 最新已是往年，需新建当年 */
  needNewYear?: boolean;
  currentYear?: number;
  signatureEnabled?: boolean;
};

const unwrap = <T>(res: unknown): T | null => {
  if (res && typeof res === 'object' && 'data' in res) {
    return (res as { data: T }).data ?? null;
  }
  return (res as T) ?? null;
};

const normalizeCrsTaxInfo = (row: UserCrsTaxInfo): UserCrsTaxInfo => {
  const id = normalizeCrsInt64Id(row.id);
  const crsId = normalizeCrsInt64Id(row.crsId);
  return {
    ...row,
    ...(id ? { id } : {}),
    ...(crsId ? { crsId } : {}),
  };
};

const normalizeCrsAttachment = (row: UserCrsAttachment): UserCrsAttachment => {
  const id = normalizeCrsInt64Id(row.id);
  return id ? { ...row, id } : { ...row };
};

/** 将 /crs/latest、/crs/submit 响应中的 int64 字段规范为字符串 */
export const normalizeCrsRecord = (raw: unknown): UserCrsRecord | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as UserCrsRecord;
  const id = normalizeCrsInt64Id(r.id);
  const userId = normalizeCrsInt64Id(r.userId);
  const baseId = normalizeCrsInt64Id(r.baseId);
  const prevId = normalizeCrsInt64Id(r.prevId);
  return {
    ...r,
    ...(id ? { id } : {}),
    ...(userId ? { userId } : {}),
    ...(baseId ? { baseId } : {}),
    ...(prevId ? { prevId } : {}),
    taxInfos: r.taxInfos?.map((item) => normalizeCrsTaxInfo(item)),
    attachments: r.attachments?.map((item) => normalizeCrsAttachment(item)),
  };
};

export const normalizeCrsLatest = (raw: unknown): UserCrsLatest | null => {
  if (!raw || typeof raw !== 'object') return null;
  const latest = raw as UserCrsLatest;
  return {
    ...latest,
    record: latest.record ? normalizeCrsRecord(latest.record) : undefined,
  };
};

const withStringBaseId = (body: UserCrsSaveReq): UserCrsSaveReq => {
  if (body.baseId == null || body.baseId === '') return body;
  const baseId = normalizeCrsInt64Id(body.baseId);
  if (!baseId) {
    const { baseId: _drop, ...rest } = body;
    return rest;
  }
  return { ...body, baseId };
};

/** GET /user/crs/latest */
export async function fetchCrsLatest() {
  const res = await http.get('/user/crs/latest');
  return normalizeCrsLatest(unwrap(res));
}

/** POST /user/crs/submit */
export async function submitCrs(body: UserCrsSaveReq) {
  const res = await http.post('/user/crs/submit', withStringBaseId(body));
  const record = unwrap<UserCrsRecord>(res);
  return record ? normalizeCrsRecord(record) : null;
}
